import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Input,
  Segmented,
  Select,
  Spin,
  Switch,
  Tag,
  Tooltip,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import TableList from "../../ui-component/Table";
import { Header } from "../../components/Header";
import { useGetData } from "../../Network/useGetQueryData";
import { usePostData } from "../../Network/usePostData";
import "./style.scss";

type Channel = "email" | "sms";
type LogFilter = "all" | "email" | "sms";
/** Generic delivery probe, or one of the two real verification flows. */
type Mode = "test" | "emailVerification" | "phoneVerification";

interface Recipient {
  id: number;
  firstName: string;
  lastName: string | null;
  email: string | null;
  phoneNumber: string | null;
  countryCode: string | null;
  roleName: string | null;
}

interface SendResult {
  transactionId: string | null;
  status: string;
  acknowledged: boolean;
  error?: string[];
  workflowId: string;
  subscriberId: string;
  channels: Channel[];
  sentToEmail: string | null;
  sentToPhone: string | null;
  isOverride: boolean;
  recipient: Recipient;
}

interface DeliveryStatus {
  found: boolean;
  code?: number;
  label?: string;
  recipient?: string;
  error?: string;
}

interface LogRow {
  id: string;
  channel: string;
  status: string;
  recipient: string | null;
  subject: string | null;
  workflow: string | null;
  provider: string | null;
  providerMessageId: string | null;
  transactionId: string | null;
  subscriberId: string | null;
  subscriberName: string | null;
  errorId: string | null;
  errorText: string | null;
  createdAt: string;
  delivery?: DeliveryStatus;
}

const statusColor: Record<string, string> = {
  sent: "success",
  warning: "warning",
  error: "error",
};

// Cequens status codes. 1 is the only one that means the handset got it.
const deliveryColor: Record<number, string> = {
  1: "success",
  2: "error",
  3: "processing",
  5: "error",
  16: "error",
  34: "default",
};

const recipientPhone = (r: Recipient) =>
  r.phoneNumber ? `${r.countryCode ?? ""}${r.phoneNumber}` : null;

const recipientLabel = (r: Recipient) => {
  const name = [r.firstName, r.lastName].filter(Boolean).join(" ");
  return `${name} · ${r.email || recipientPhone(r) || `#${r.id}`}`;
};

const NotificationTestPage = () => {
  const { t } = useTranslation();

  const searchTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const [mode, setMode] = useState<Mode>("test");
  const [searchKey, setSearchKey] = useState<string>();
  const [userId, setUserId] = useState<number>();
  const [channels, setChannels] = useState<Channel[]>(["email"]);
  const [message, setMessage] = useState("");
  const [useOverride, setUseOverride] = useState(false);
  const [overrideEmail, setOverrideEmail] = useState("");
  const [overridePhone, setOverridePhone] = useState("");
  const [result, setResult] = useState<SendResult>();
  const [notice, setNotice] = useState<string>();
  const [errorMessage, setErrorMessage] = useState<string>();

  const [logFilter, setLogFilter] = useState<LogFilter>("all");
  const [onlyThisUser, setOnlyThisUser] = useState(false);
  const [withDelivery, setWithDelivery] = useState(true);
  // Novu processes the trigger asynchronously, so the log is polled for a
  // short while after a send instead of being fetched once.
  const [pollUntil, setPollUntil] = useState(0);

  const isTest = mode === "test";

  const { data: recipientsData, isLoading: recipientsLoading } = useGetData<{
    data: Recipient[];
  }>({
    queryKey: ["notification-test-recipients", { searchKey }],
    url: "/notification-test/recipients",
    params: { searchKey, limit: 20 },
  });

  const recipients = useMemo(
    () => recipientsData?.data || [],
    [recipientsData],
  );
  const selected = recipients.find((r) => r.id === userId);

  const {
    data: logData,
    isLoading: logLoading,
    isFetching: logFetching,
    refetch: refetchLog,
  } = useGetData<{ data: { items: LogRow[]; hasMore: boolean } }>({
    queryKey: [
      "notification-test-log",
      { logFilter, onlyThisUser, userId, withDelivery },
    ],
    url: "/notification-test/log",
    params: {
      channel: logFilter === "all" ? undefined : logFilter,
      subscriberId: onlyThisUser && userId ? String(userId) : undefined,
      withDeliveryStatus: withDelivery,
      limit: 30,
    },
    // No queryKey here: useGetData spreads options last, so passing one
    // would clobber the computed key and the filters would stop refetching.
    options: {
      refetchInterval: pollUntil > Date.now() ? 3000 : false,
    } as any,
  });

  useEffect(() => {
    if (!pollUntil) return;
    const timer = setTimeout(
      () => setPollUntil(0),
      Math.max(pollUntil - Date.now(), 0),
    );
    return () => clearTimeout(timer);
  }, [pollUntil]);

  const clearResult = () => {
    setResult(undefined);
    setNotice(undefined);
    setErrorMessage(undefined);
  };

  useEffect(clearResult, [mode]);

  const startPolling = () => setPollUntil(Date.now() + 30000);

  const onError = (error: any) => {
    setResult(undefined);
    setNotice(undefined);
    const raw = error?.response?.data?.message;
    setErrorMessage(
      (Array.isArray(raw) ? raw.join(", ") : raw) ||
        error?.message ||
        t("notificationTest.send.failed"),
    );
  };

  const { mutate: sendTest, isPending: sendingTest } = usePostData<
    { data: SendResult },
    Record<string, unknown>
  >({
    url: "/notification-test/send",
    options: {
      hideMessage: true,
      onSuccess: (response) => {
        clearResult();
        setResult(response.data);
        startPolling();
      },
      onError,
    },
  });

  const { mutate: sendEmailVerification, isPending: sendingEmail } = usePostData<
    unknown,
    Record<string, unknown>
  >({
    url: "/user/send-email-verification",
    options: {
      hideMessage: true,
      onSuccess: () => {
        clearResult();
        setNotice(
          t("notificationTest.verification.emailSent", {
            email: selected?.email,
          }),
        );
        startPolling();
      },
      onError,
    },
  });

  const { mutate: sendPhoneVerification, isPending: sendingPhone } = usePostData<
    unknown,
    Record<string, unknown>
  >({
    url: "/phone-number/send-verification-code",
    options: {
      hideMessage: true,
      onSuccess: () => {
        clearResult();
        setNotice(
          t("notificationTest.verification.smsSent", {
            phone: selected ? recipientPhone(selected) : "",
          }),
        );
        startPolling();
      },
      onError,
    },
  });

  const isPending = sendingTest || sendingEmail || sendingPhone;

  const needsEmail = isTest ? channels.includes("email") : mode === "emailVerification";
  const needsPhone = isTest ? channels.includes("sms") : mode === "phoneVerification";

  const missingEmail =
    needsEmail && !selected?.email && !(isTest && useOverride && overrideEmail);
  const missingPhone =
    needsPhone &&
    !selected?.phoneNumber &&
    !(isTest && useOverride && overridePhone);

  const canSend =
    !!userId &&
    (!isTest || channels.length > 0) &&
    !missingEmail &&
    !missingPhone;

  const onSend = () => {
    if (!canSend || !selected) return;

    if (mode === "emailVerification") {
      sendEmailVerification({ userId });
      return;
    }

    if (mode === "phoneVerification") {
      sendPhoneVerification({
        firstName: selected.firstName,
        phoneNumber: selected.phoneNumber,
        countryCode: selected.countryCode,
        email: selected.email || undefined,
      });
      return;
    }

    sendTest({
      userId,
      channels,
      message: message || undefined,
      overrideEmail: useOverride && overrideEmail ? overrideEmail : undefined,
      overridePhone: useOverride && overridePhone ? overridePhone : undefined,
    });
  };

  const columns: ColumnsType<LogRow> = [
    {
      title: t("notificationTest.table.sentAt"),
      key: "createdAt",
      width: 140,
      render: (_, row) => (
        <p>{dayjs(row.createdAt).format("DD/MM/YYYY HH:mm")}</p>
      ),
    },
    {
      title: t("notificationTest.table.channel"),
      key: "channel",
      width: 90,
      render: (_, row) => (
        <Tag color={row.channel === "email" ? "blue" : "purple"}>
          {row.channel}
        </Tag>
      ),
    },
    {
      title: t("notificationTest.table.recipient"),
      key: "recipient",
      width: 260,
      ellipsis: true,
      render: (_, row) => (
        <div>
          <p>{row.recipient || "-"}</p>
          {row.subscriberName && (
            <p className="notification-test-muted">{row.subscriberName}</p>
          )}
        </div>
      ),
    },
    {
      title: t("notificationTest.table.workflow"),
      key: "workflow",
      width: 200,
      render: (_, row) => (
        <div>
          <p>{row.workflow || "-"}</p>
          {row.subject && (
            <p className="notification-test-muted">{row.subject}</p>
          )}
        </div>
      ),
    },
    {
      title: t("notificationTest.table.novuStatus"),
      key: "status",
      width: 150,
      render: (_, row) => (
        <div>
          <Tag color={statusColor[row.status] || "default"}>{row.status}</Tag>
          {row.errorId && (
            <Tooltip title={row.errorText || row.errorId}>
              <p className="notification-test-error">{row.errorId}</p>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: t("notificationTest.table.delivery"),
      key: "delivery",
      width: 160,
      render: (_, row) => {
        if (row.channel !== "sms") {
          return (
            <Tooltip title={t("notificationTest.table.emailDeliveryHint")}>
              <p className="notification-test-muted">—</p>
            </Tooltip>
          );
        }
        if (!row.delivery) {
          return (
            <p className="notification-test-muted">
              {withDelivery ? "-" : t("notificationTest.table.deliveryOff")}
            </p>
          );
        }
        if (!row.delivery.found) {
          return (
            <Tooltip
              title={
                row.delivery.error || t("notificationTest.table.deliveryExpired")
              }
            >
              <p className="notification-test-muted">
                {t("notificationTest.table.deliveryUnknown")}
              </p>
            </Tooltip>
          );
        }
        return (
          <Tag color={deliveryColor[row.delivery.code as number] || "default"}>
            {row.delivery.label}
          </Tag>
        );
      },
    },
    {
      title: t("notificationTest.table.provider"),
      key: "provider",
      width: 130,
      render: (_, row) => <p>{row.provider || "-"}</p>,
    },
  ];

  return (
    <>
      <Header title={t("notificationTest.header.title")} />

      <div className="notification-test-page">
        <div className="notification-test-card">
          <div className="notification-test-card-head">
            <h3>{t("notificationTest.form.title")}</h3>
            <Segmented
              value={mode}
              onChange={(value) => setMode(value as Mode)}
              options={[
                { label: t("notificationTest.mode.test"), value: "test" },
                {
                  label: t("notificationTest.mode.emailVerification"),
                  value: "emailVerification",
                },
                {
                  label: t("notificationTest.mode.phoneVerification"),
                  value: "phoneVerification",
                },
              ]}
            />
          </div>
          <p className="notification-test-muted">
            {t(`notificationTest.mode.${mode}Hint`)}
          </p>

          <div className="notification-test-form-row">
          <div className="notification-test-field notification-test-field-user">
            <label>{t("notificationTest.form.user")}</label>
            <Select
              showSearch
              allowClear
              value={userId}
              placeholder={t("notificationTest.form.userPlaceholder")}
              filterOption={false}
              notFoundContent={recipientsLoading ? <Spin size="small" /> : null}
              onSearch={(value) => {
                clearTimeout(searchTimer.current);
                searchTimer.current = setTimeout(
                  () => setSearchKey(value),
                  300,
                );
              }}
              onChange={(value) => setUserId(value)}
              options={recipients.map((r) => ({
                value: r.id,
                label: recipientLabel(r),
              }))}
            />
          </div>

          {isTest && (
            <>
              <div className="notification-test-field notification-test-field-channels">
                <label>{t("notificationTest.form.channels")}</label>
                <Checkbox.Group
                  value={channels}
                  onChange={(values) => setChannels(values as Channel[])}
                  options={[
                    {
                      label: t("notificationTest.channel.email"),
                      value: "email",
                    },
                    { label: t("notificationTest.channel.sms"), value: "sms" },
                  ]}
                />
              </div>

              <div className="notification-test-field notification-test-field-message">
                <label>{t("notificationTest.form.message")}</label>
                <Input.TextArea
                  rows={1}
                  maxLength={300}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t("notificationTest.form.messagePlaceholder")}
                />
              </div>
            </>
          )}

          <div className="notification-test-field notification-test-field-action">
            <Button
              type="primary"
              size="large"
              loading={isPending}
              disabled={!canSend}
              onClick={onSend}
            >
              {t(`notificationTest.form.send${isTest ? "" : "Verification"}`)}
            </Button>
          </div>
          </div>

          {selected && (
            <div className="notification-test-selected">
              <span>
                {t("notificationTest.form.email")}:{" "}
                <b>{selected.email || t("notificationTest.form.none")}</b>
              </span>
              <span>
                {t("notificationTest.form.phone")}:{" "}
                <b>
                  {recipientPhone(selected) || t("notificationTest.form.none")}
                </b>
              </span>
              {selected.roleName && <span>{selected.roleName}</span>}
            </div>
          )}

          {isTest && (
            <>
              <div className="notification-test-override">
                <div className="notification-test-override-head">
                  <Switch
                    size="small"
                    checked={useOverride}
                    onChange={setUseOverride}
                  />
                  <span>{t("notificationTest.form.override")}</span>
                </div>
                {useOverride && (
                  <>
                    <p className="notification-test-muted">
                      {t("notificationTest.form.overrideHint")}
                    </p>
                    <div className="notification-test-override-inputs">
                      <Input
                        value={overrideEmail}
                        onChange={(e) => setOverrideEmail(e.target.value)}
                        placeholder={t("notificationTest.form.overrideEmail")}
                      />
                      <Input
                        value={overridePhone}
                        onChange={(e) => setOverridePhone(e.target.value)}
                        placeholder={t("notificationTest.form.overridePhone")}
                      />
                    </div>
                  </>
                )}
              </div>
            </>
          )}

          {(missingEmail || missingPhone) && (
            <Alert
              type="warning"
              showIcon
              message={
                missingEmail
                  ? t("notificationTest.form.noEmail")
                  : t("notificationTest.form.noPhone")
              }
            />
          )}

          {errorMessage && (
            <Alert type="error" showIcon message={errorMessage} />
          )}

          {notice && (
            <Alert
              type="success"
              showIcon
              message={notice}
              description={
                <p className="notification-test-muted">
                  {t("notificationTest.result.hint")}
                </p>
              }
            />
          )}

          {result && (
            <Alert
              type={result.status === "processed" ? "success" : "warning"}
              showIcon
              message={t("notificationTest.result.title", {
                status: result.status,
              })}
              description={
                <div className="notification-test-result">
                  {result.sentToEmail && (
                    <p>
                      {t("notificationTest.channel.email")}:{" "}
                      <b>{result.sentToEmail}</b>
                    </p>
                  )}
                  {result.sentToPhone && (
                    <p>
                      {t("notificationTest.channel.sms")}:{" "}
                      <b>{result.sentToPhone}</b>
                    </p>
                  )}
                  <p>
                    {t("notificationTest.result.transactionId")}:{" "}
                    <code>{result.transactionId || "-"}</code>
                  </p>
                  {result.isOverride && (
                    <p>{t("notificationTest.result.overrideUsed")}</p>
                  )}
                  {result.error?.length ? (
                    <p className="notification-test-error">
                      {result.error.join(", ")}
                    </p>
                  ) : null}
                  <p className="notification-test-muted">
                    {t("notificationTest.result.hint")}
                  </p>
                </div>
              }
            />
          )}
        </div>

        <div className="notification-test-log">
          <div className="notification-test-log-head">
            <h3>{t("notificationTest.log.title")}</h3>
            <div className="notification-test-log-controls">
              <Segmented
                value={logFilter}
                onChange={(value) => setLogFilter(value as LogFilter)}
                options={[
                  { label: t("notificationTest.log.all"), value: "all" },
                  { label: t("notificationTest.channel.email"), value: "email" },
                  { label: t("notificationTest.channel.sms"), value: "sms" },
                ]}
              />
              <label className="notification-test-toggle">
                <Switch
                  size="small"
                  checked={onlyThisUser}
                  disabled={!userId}
                  onChange={setOnlyThisUser}
                />
                {t("notificationTest.log.onlyThisUser")}
              </label>
              <label className="notification-test-toggle">
                <Switch
                  size="small"
                  checked={withDelivery}
                  onChange={setWithDelivery}
                />
                {t("notificationTest.log.withDelivery")}
              </label>
              <Button onClick={() => refetchLog()} loading={logFetching}>
                {t("notificationTest.log.refresh")}
              </Button>
            </div>
          </div>

          {pollUntil > Date.now() && (
            <p className="notification-test-muted">
              {t("notificationTest.log.polling")}
            </p>
          )}

          <TableList
            data={logData?.data?.items || []}
            columns={columns}
            loading={logLoading}
            rowKey="id"
            height="calc(100vh - 470px)"
            // Fixed layout: otherwise antd re-measures on every filter change
            // and the recipient column keeps growing.
            tableLayout="fixed"
          />
        </div>
      </div>
    </>
  );
};

export default NotificationTestPage;
